import { supabase } from '../lib/supabase';
import { activityService } from './activityService';
import { notificationService } from './notificationService';
import { DEMO_ERROR_MESSAGE, isDemoUser } from '../utils/demoMode';

// ─── Column mapping helpers ───────────────────────────────────────────────────
// DB uses: task_date, created_by_user_id, is_completed, status
// App uses: date, created_by, status (pending/completed/blocked)

const mapTaskFromDB = (row) => ({
  id: row?.id,
  site_id: row?.site_id,
  date: row?.task_date,
  created_by: row?.created_by_user_id,
  assigned_to_user_id: row?.assigned_to_user_id,
  title: row?.title,
  description: row?.description || '',
  category: row?.category || 'Operations',
  required: row?.required ?? true,
  status: row?.status || (row?.is_completed ? 'completed' : 'pending'),
  completed_at: row?.completed_at || null,
  completed_by: row?.completed_by_user_id || null,
  notes: row?.notes || '',
  photo_required: row?.photo_required ?? false,
  min_photos: row?.min_photos ?? 0,
  photo_url: row?.photo_url || null,
  due_time: row?.due_time || null,
  company_id: row?.company_id || null,
  work_carried_out_by: row?.work_carried_out_by || null,
  // joined data
  assigned_user: row?.assigned_user || null,
  completed_by_user: row?.completed_by_user || null,
  site: row?.site || null,
});

const mapTaskToDB = (task) => ({
  title: task?.title,
  description: task?.description || '',
  site_id: task?.site_id,
  assigned_to_user_id: task?.assigned_to_user_id,
  created_by_user_id: task?.created_by,
  task_date: task?.date,
  category: task?.category || 'Operations',
  required: task?.required ?? true,
  status: task?.status || 'pending',
  is_completed: task?.status === 'completed',
  completed_at: task?.completed_at || null,
  completed_by_user_id: task?.completed_by || null,
  notes: task?.notes || '',
  photo_required: task?.photo_required ?? false,
  min_photos: task?.photo_required ? Math.max(task?.min_photos ?? 1, 1) : 0,
  photo_url: task?.photo_url || null,
  due_time: task?.due_time || null,
  company_id: task?.company_id || null,
  work_carried_out_by: task?.work_carried_out_by || null,
});

const mapTemplateFromDB = (row) => ({
  id: row?.id,
  title: row?.title,
  description: row?.description || '',
  default_category: row?.default_category || 'Operations',
  default_required: row?.default_required ?? true,
  estimated_minutes: row?.estimated_minutes || 20,
  photo_required: row?.photo_required ?? false,
  usage_count: row?.usage_count || 0,
  is_global: row?.is_global ?? true,
  company_id: row?.company_id || null,
  created_by: row?.created_by || null,
});

// ─── Task Templates ───────────────────────────────────────────────────────────

export const taskTemplateService = {
  async getAll() {
    try {
      const { data, error } = await supabase?.from('task_templates')?.select('*')?.order('usage_count', { ascending: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapTemplateFromDB), error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load templates' };
    }
  },

  async create(template) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      const { data, error } = await supabase?.from('task_templates')?.insert({
          title: template?.title,
          description: template?.description || '',
          default_category: template?.default_category || 'Operations',
          default_required: template?.default_required ?? true,
          estimated_minutes: template?.estimated_minutes || 20,
          photo_required: template?.photo_required ?? false,
          is_global: false,
          created_by: user?.id,
        })?.select()?.single();
      if (error) throw error;
      return { data: mapTemplateFromDB(data), error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to create template' };
    }
  },

  async incrementUsage(templateId) {
    try {
      await supabase?.rpc('increment_template_usage', { template_id: templateId });
    } catch {
      // non-critical
    }
  },
};

// ─── Daily Tasks ──────────────────────────────────────────────────────────────

export const dailyTaskService = {
  async getByDateAndSite(date, siteId) {
    try {
      let query = supabase?.from('daily_tasks')?.select(`
          *,
          assigned_user:user_profiles!daily_tasks_assigned_to_user_id_fkey(id, full_name, role),
          completed_by_user:user_profiles!daily_tasks_completed_by_user_id_fkey(id, full_name),
          site:sites!daily_tasks_site_id_fkey(id, site_name)
        `)?.eq('task_date', date);
      if (siteId && siteId !== 'all') {
        query = query?.eq('site_id', siteId);
      }
      const { data, error } = await query?.order('due_time', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapTaskFromDB), error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load tasks' };
    }
  },

  async getForWorkerToday(userId, date) {
    try {
      const today = date || new Date()?.toISOString()?.split('T')?.[0];
      // Use a 7-day rolling window so demo/seeded tasks remain visible
      // even if the demo data was seeded on a prior day
      const sevenDaysAgo = new Date(new Date(today).getTime() - 7 * 86400000)?.toISOString()?.split('T')?.[0];
      const { data, error } = await supabase?.from('daily_tasks')?.select(`
          *,
          completed_by_user:user_profiles!daily_tasks_completed_by_user_id_fkey(id, full_name),
          site:sites!daily_tasks_site_id_fkey(id, site_name)
        `)?.eq('assigned_to_user_id', userId)
        ?.gte('task_date', sevenDaysAgo)
        ?.lte('task_date', today)
        ?.order('task_date', { ascending: false })
        ?.order('due_time', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapTaskFromDB), error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load tasks' };
    }
  },

  async getForSite(siteId, date) {
    try {
      const { data, error } = await supabase?.from('daily_tasks')?.select(`
          *,
          assigned_user:user_profiles!daily_tasks_assigned_to_user_id_fkey(id, full_name, role),
          completed_by_user:user_profiles!daily_tasks_completed_by_user_id_fkey(id, full_name)
        `)?.eq('site_id', siteId)?.eq('task_date', date)?.order('due_time', { ascending: true, nullsFirst: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapTaskFromDB), error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load site tasks' };
    }
  },

  async getForDateRange(startDate, endDate, siteId) {
    try {
      let query = supabase?.from('daily_tasks')?.select('*')?.gte('task_date', startDate)?.lte('task_date', endDate);
      if (siteId && siteId !== 'all') {
        query = query?.eq('site_id', siteId);
      }
      const { data, error } = await query?.order('task_date', { ascending: false });
      if (error) throw error;
      return { data: (data || [])?.map(mapTaskFromDB), error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to load tasks' };
    }
  },

  /**
   * Return the most recent task_date for a site, or null if the site has
   * no tasks. Used by the Task Planner to default its date picker to a
   * day that actually has data instead of always starting at today.
   */
  async getLatestTaskDate(siteId) {
    try {
      if (!siteId) return { data: null, error: null };
      const { data, error } = await supabase
        ?.from('daily_tasks')
        ?.select('task_date')
        ?.eq('site_id', siteId)
        ?.order('task_date', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();
      if (error) throw error;
      return { data: data?.task_date || null, error: null };
    } catch (err) {
      return { data: null, error: err?.message };
    }
  },

  // Return { total, completed } counts for a site across all dates.
  // Used by the site-detail Overview tab to show "Work Completed"
  // alongside "Schedule Progress". Two head:true count queries keep
  // the payload tiny on sites with hundreds of tasks.
  async getCountsForSite(siteId) {
    try {
      if (!siteId) return { data: { total: 0, completed: 0 }, error: null };
      const [totalRes, completedRes] = await Promise.all([
        supabase
          ?.from('daily_tasks')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('site_id', siteId),
        supabase
          ?.from('daily_tasks')
          ?.select('id', { count: 'exact', head: true })
          ?.eq('site_id', siteId)
          ?.eq('status', 'completed'),
      ]);
      if (totalRes?.error) throw totalRes.error;
      if (completedRes?.error) throw completedRes.error;
      return {
        data: { total: totalRes?.count || 0, completed: completedRes?.count || 0 },
        error: null,
      };
    } catch (err) {
      return { data: { total: 0, completed: 0 }, error: err?.message };
    }
  },

  /**
   * Return the site id with the most recent task activity across the
   * company. Used by the Task Planner to default its site picker to
   * where work is actually happening, rather than the newest active
   * site (which is often empty for companies that create sites ahead
   * of scheduling tasks). Returns `{ data: null, error: null }` when
   * the company has no tasks anywhere yet — caller falls back to the
   * current "newest site" behaviour.
   *
   * Errors are surfaced (not swallowed as empty data) so silent
   * schema/RLS regressions show up immediately instead of quietly
   * steering the UI back to the buggy default.
   */
  async getSiteWithLatestActivity(companyId) {
    try {
      if (!companyId) return { data: null, error: null };
      // `daily_tasks` has no `created_at`; tiebreak on `completed_at` so the
      // most-recently-finished task wins when two sites share the top
      // task_date. Supabase's `{ ascending: false }` places NULLs last, so
      // pending tasks don't shadow completed ones.
      const { data, error } = await supabase
        ?.from('daily_tasks')
        ?.select('site_id, sites!inner(company_id, status)')
        ?.eq('sites.company_id', companyId)
        ?.eq('sites.status', 'active')
        ?.order('task_date', { ascending: false })
        ?.order('completed_at', { ascending: false })
        ?.limit(1)
        ?.maybeSingle();
      if (error) throw error;
      return { data: data?.site_id || null, error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to look up site with latest activity' };
    }
  },

  async createMany(tasks) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      // Demo mode guard — surface an explicit error instead of fake success.
      // error stays a string to match this service's existing { data, error }
      // shape (callers wrap it into an Error / sanitizeError).
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: [], error: DEMO_ERROR_MESSAGE };
      }
      const rows = tasks?.map(t => mapTaskToDB({ ...t, created_by: t?.created_by || user?.id }));
      const { data, error } = await supabase?.from('daily_tasks')?.insert(rows)?.select(`
        *,
        assigned_user:user_profiles!daily_tasks_assigned_to_user_id_fkey(id, full_name, email),
        completed_by_user:user_profiles!daily_tasks_completed_by_user_id_fkey(id, full_name),
        site:sites!daily_tasks_site_id_fkey(id, site_name)
      `);
      if (error) throw error;

      // Send assignment emails and in-app notifications for tasks with assignees
      const creatorProfile = await supabase
        ?.from('user_profiles')
        ?.select('full_name')
        ?.eq('id', user?.id)
        ?.maybeSingle();
      for (const task of data || []) {
        if (task?.assigned_user?.email) {
          notificationService?.sendTaskAssigned({
            taskId: task?.id,
            assigneeEmail: task?.assigned_user?.email,
            assigneeName: task?.assigned_user?.full_name,
            assigneeUserId: task?.assigned_user?.id,
            assignerName: creatorProfile?.data?.full_name || 'A team member',
            taskTitle: task?.title,
            taskDescription: task?.description || '',
            category: task?.category || 'General',
            siteName: task?.site?.site_name || '',
            dueDate: task?.task_date || '',
          })?.catch(() => {}); // fire-and-forget
        }
        // In-app notification for task assignment
        if (task?.assigned_to_user_id) {
          notificationService?.createNotification(
            task?.assigned_to_user_id,
            'task_assigned',
            'Task Assigned',
            `You have been assigned: ${task?.title}${task?.site?.site_name ? ' on ' + task?.site?.site_name : ''}`,
            '/task-completion-workflow',
            task?.id,
            task?.site_id
          )?.catch(() => {});
        }
      }

      return { data: (data || [])?.map(mapTaskFromDB), error: null };
    } catch (err) {
      return { data: [], error: err?.message || 'Failed to create tasks' };
    }
  },

  async updateStatus(taskId, status, notes, completedBy, workCarriedOutBy) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      // Demo mode guard — surface an explicit error instead of fake success.
      if (isDemoUser(user?.email)) {
        window.dispatchEvent(new CustomEvent('demo-mode-action'));
        return { data: null, error: DEMO_ERROR_MESSAGE };
      }
      const updates = {
        status,
        is_completed: status === 'completed',
        notes: notes || '',
      };
      if (status === 'completed') {
        updates.completed_at = new Date()?.toISOString();
        updates.completed_by_user_id = completedBy || null;
        // Free-text "who physically did the work"; may differ from the
        // logger (completed_by_user_id). NULL means "same as logger".
        updates.work_carried_out_by = workCarriedOutBy?.trim() || null;
      } else {
        updates.completed_at = null;
        updates.completed_by_user_id = null;
        updates.work_carried_out_by = null;
      }
      const { data, error } = await supabase?.from('daily_tasks')?.update(updates)?.eq('id', taskId)?.select(`
        *,
        site:sites!daily_tasks_site_id_fkey(id, site_name, company_id)
      `)?.single();
      if (error) throw error;

      // Log activity when task is completed
      if (status === 'completed' && data?.site_id) {
        await activityService?.createActivity({
          companyId: data?.site?.company_id || data?.company_id,
          siteId: data?.site_id,
          activityType: 'task_completed',
          title: `Task completed: ${data?.title}`,
          description: `Category: ${data?.category || 'General'}${notes ? ' | Notes: ' + notes : ''}`,
          relatedEntityId: data?.id,
          metadata: { taskId: data?.id, category: data?.category, required: data?.required }
        });
      }

      // Log activity + fan out notifications when task is blocked. Admins and
      // site_managers in the same company get an in-app alert with the
      // worker's note so they can act on whatever is blocking work.
      if (status === 'blocked' && data?.site_id) {
        const companyId = data?.site?.company_id || data?.company_id;

        await activityService?.createActivity({
          companyId,
          siteId: data?.site_id,
          activityType: 'task_blocked',
          title: `Task blocked: ${data?.title}`,
          description: notes ? `Reason: ${notes}` : '',
          relatedEntityId: data?.id,
          metadata: { taskId: data?.id, category: data?.category },
        });

        const { data: actor } = await supabase
          ?.from('user_profiles')
          ?.select('full_name')
          ?.eq('id', completedBy)
          ?.maybeSingle();

        await notificationService?.notifyTaskBlocked(
          data?.id,
          data?.site_id,
          companyId,
          data?.title,
          actor?.full_name,
          notes
        );
      }

      // In-app notification for overdue task (when status is set to overdue or task is past due)
      if (status === 'overdue' && data?.assigned_to_user_id) {
        notificationService?.createNotification(
          data?.assigned_to_user_id,
          'task_overdue',
          'Task Overdue',
          `Task "${data?.title}" is overdue${data?.site?.site_name ? ' on ' + data?.site?.site_name : ''}`,
          '/task-completion-workflow',
          data?.id,
          data?.site_id
        )?.catch(() => {});
      }

      return { data: mapTaskFromDB(data), error: null };
    } catch (err) {
      return { data: null, error: err?.message || 'Failed to update task' };
    }
  },

  async delete(taskId) {
    try {
      const { error } = await supabase?.from('daily_tasks')?.delete()?.eq('id', taskId);
      if (error) throw error;
      return { error: null };
    } catch (err) {
      return { error: err?.message || 'Failed to delete task' };
    }
  },

  // Compute stats from a task array (client-side helpers)
  getCompletionRate(tasks) {
    if (!tasks?.length) return 0;
    const completed = tasks?.filter(t => t?.status === 'completed')?.length;
    return Math.round((completed / tasks?.length) * 100);
  },

  getRequiredCompletionRate(tasks) {
    const required = tasks?.filter(t => t?.required) || [];
    if (!required?.length) return 100;
    const completed = required?.filter(t => t?.status === 'completed')?.length;
    return Math.round((completed / required?.length) * 100);
  },

  getOverdueTasks(tasks) {
    const now = new Date();
    return (tasks || [])?.filter(t => {
      if (t?.status === 'completed') return false;
      if (!t?.due_time) return false;
      const [h, m] = t?.due_time?.split(':')?.map(Number);
      const due = new Date();
      due?.setHours(h, m, 0, 0);
      return now > due;
    });
  },

  groupByWorker(tasks, workers) {
    return (workers || [])?.map(worker => {
      const wTasks = (tasks || [])?.filter(t => t?.assigned_to_user_id === worker?.id);
      const completed = wTasks?.filter(t => t?.status === 'completed')?.length;
      const overdue = this.getOverdueTasks(wTasks)?.length;
      const rate = wTasks?.length > 0 ? Math.round((completed / wTasks?.length) * 100) : 0;
      return { ...worker, tasks_total: wTasks?.length, tasks_completed: completed, tasks_overdue: overdue, completion_rate: rate };
    })?.filter(w => w?.tasks_total > 0);
  },

  groupBySite(tasks, sites) {
    return (sites || [])?.map(site => {
      const sTasks = (tasks || [])?.filter(t => t?.site_id === site?.id);
      const completed = sTasks?.filter(t => t?.status === 'completed')?.length;
      const overdue = this.getOverdueTasks(sTasks)?.length;
      const rate = sTasks?.length > 0 ? Math.round((completed / sTasks?.length) * 100) : 0;
      return { ...site, tasks_total: sTasks?.length, tasks_completed: completed, tasks_overdue: overdue, completion_rate: rate };
    });
  },
};

function taskService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: taskService is not implemented yet.', args);
  return null;
}

export { taskService };